import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { ThirdPartyPayoffs } from '../../../../../components/FlowOfFunds/ThirdPartyPayoffs/ThirdPartyPayoffs';
import { WireInstructionsModal } from '../../../../../components/FlowOfFunds/WireInstructionsModal/WireInstructionsModal';
import { RemovalProposalModal } from '../../../../../components/FlowOfFunds/RemovalProposalModal/RemovalProposalModal';
import { ClosingTickler } from '../../../../../components/FlowOfFunds/ClosingTickler/ClosingTickler';
import { useFlowOfFunds } from '../useFlowOfFunds';
import {
    ApprovalState,
    SaveThirdPartyPayoffRowRequest,
    ThirdPartyPayoffDocumentKind,
    WireInstructions,
} from '../../../../../models/FlowOfFunds/flow-of-funds.model';
import createNotification from '../../../../../utils/createNotification';
import { GetFlowOfFunds } from '../../../../../slices/marketplace/flow-of-funds/thunks/get-flow-of-funds.thunk';
import { AddThirdPartyPayoffRow } from '../../../../../slices/marketplace/flow-of-funds/thunks/add-third-party-payoff-row.thunk';
import {
    SaveThirdPartyPayoffRow,
    SaveThirdPartyPayoffRowRejection,
} from '../../../../../slices/marketplace/flow-of-funds/thunks/save-third-party-payoff-row.thunk';
import { UpdateThirdPartyPayoffWireInstructions } from '../../../../../slices/marketplace/flow-of-funds/thunks/update-third-party-payoff-wire-instructions.thunk';
import { UploadThirdPartyPayoffSupportingDocument } from '../../../../../slices/marketplace/flow-of-funds/thunks/upload-third-party-payoff-supporting-document.thunk';
import { DeleteThirdPartyPayoffSupportingDocument } from '../../../../../slices/marketplace/flow-of-funds/thunks/delete-third-party-payoff-supporting-document.thunk';
import { DownloadThirdPartyPayoffSupportingDocument } from '../../../../../slices/marketplace/flow-of-funds/thunks/download-third-party-payoff-supporting-document.thunk';
import { SetThirdPartyPayoffApproval } from '../../../../../slices/marketplace/flow-of-funds/thunks/set-third-party-payoff-approval.thunk';
import { SetThirdPartyPayoffBuyerAssumption } from '../../../../../slices/marketplace/flow-of-funds/thunks/set-third-party-payoff-buyer-assumption.thunk';
import { SetThirdPartyPayoffPayoffAmount } from '../../../../../slices/marketplace/flow-of-funds/thunks/set-third-party-payoff-payoff-amount.thunk';
import { GetContractDocumentSource } from '../../../../../slices/marketplace/flow-of-funds/thunks/get-contract-document-source.thunk';
import { ProposeThirdPartyPayoffRemoval } from '../../../../../slices/marketplace/flow-of-funds/thunks/propose-third-party-payoff-removal.thunk';
import { RespondToThirdPartyPayoffRemoval } from '../../../../../slices/marketplace/flow-of-funds/thunks/respond-to-third-party-payoff-removal.thunk';
import {
    ThirdPartyPayoffRowSaveResult,
    thirdPartyPayoffsMutationToast,
} from '../../../../../components/FlowOfFunds/ThirdPartyPayoffs/useThirdPartyPayoffsAutosave';

export interface ThirdPartyPayoffsSubTabProps {
    engagementId: number;
}

const toTargetApprovalState = (isApproved: boolean | null): ApprovalState => {
    if (isApproved === true) {
        return ApprovalState.Approved;
    }
    if (isApproved === false) {
        return ApprovalState.Rejected;
    }
    return ApprovalState.Pending;
};

// Types a browser renders itself — open these in a tab so a pay-off letter is
// read in place. The API decides this too (Content-Disposition), but the blob's
// own type is what the tab actually honours, so the rule lives on both sides.
const INLINE_VIEWABLE_TYPES = ['application/pdf', 'text/plain'];

const isViewableInBrowser = (contentType: string) =>
    INLINE_VIEWABLE_TYPES.includes(contentType) ||
    (contentType.startsWith('image/') && !contentType.includes('svg'));

// The document sits behind the Bearer guard, so it cannot be a plain href — it is
// fetched through core.axios and handed to the tab as a blob URL. `opener = null`
// per the app's other window.open call sites (reverse-tabnabbing guard). SVG and
// anything script-bearing is deliberately downloaded instead of rendered: a blob
// URL inherits our origin, so rendering an uploaded document there would be
// stored XSS.
const openUrlInNewTab = (url: string) => {
    const viewer = window.open(url, '_blank');
    if (viewer) {
        viewer.opener = null;
        return;
    }
    createNotification('Allow pop-ups for this site to open the document.', 'warning');
};

const openBlobInNewTab = (blob: Blob, contentType: string, fileName: string) => {
    const url = window.URL.createObjectURL(new Blob([blob], { type: contentType }));

    if (isViewableInBrowser(contentType)) {
        openUrlInNewTab(url);
    } else {
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    // The tab reads the blob asynchronously, so revoking immediately can race it.
    window.setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
};

export const ThirdPartyPayoffsSubTab = (props: ThirdPartyPayoffsSubTabProps) => {
    const dispatch = useDispatch() as any;
    const { flowOfFunds, isLoading } = useFlowOfFunds(props.engagementId);
    const [wireInstructionsRowId, setWireInstructionsRowId] = useState<number | null>(null);
    const [removalRowId, setRemovalRowId] = useState<number | null>(null);

    const engagementId = props.engagementId;

    if (isLoading) {
        return <div className="mt-4 text-muted">Loading…</div>;
    }
    if (!flowOfFunds?.thirdPartyPayoffs) {
        return (
            <div className="mt-4 text-muted">
                No third-party pay-offs have been recorded for this engagement yet.
            </div>
        );
    }

    const section = flowOfFunds.thirdPartyPayoffs;
    const wireInstructionsRow = section.rows.find((r) => r.tableRowId === wireInstructionsRowId) ?? null;

    const mutateWithSaveToast = async (
        actionPromise: Promise<any>,
        isFulfilled: (action: any) => boolean
    ) => {
        thirdPartyPayoffsMutationToast.saving();
        const action = await actionPromise;
        if (isFulfilled(action)) {
            thirdPartyPayoffsMutationToast.saved();
        } else {
            thirdPartyPayoffsMutationToast.failed();
        }
        return action;
    };

    const addRow = async () => {
        const action = await mutateWithSaveToast(
            dispatch(AddThirdPartyPayoffRow({ engagementId })),
            AddThirdPartyPayoffRow.fulfilled.match
        );
        if (AddThirdPartyPayoffRow.fulfilled.match(action)) {
            dispatch(GetFlowOfFunds(engagementId));
        }
    };

    const saveRow = async (
        tableRowId: number,
        request: SaveThirdPartyPayoffRowRequest
    ): Promise<ThirdPartyPayoffRowSaveResult> => {
        const action = await dispatch(SaveThirdPartyPayoffRow({ engagementId, tableRowId, ...request }));
        if (SaveThirdPartyPayoffRow.fulfilled.match(action)) {
            return { outcome: 'ok', freshCells: action.payload.cells };
        }
        const rejection = action.payload as SaveThirdPartyPayoffRowRejection | undefined;
        if (rejection?.isConflict) {
            dispatch(GetFlowOfFunds(engagementId));
            createNotification(
                'This row was changed by the other party — refreshed with the latest values.',
                'error'
            );
            return { outcome: 'conflict' };
        }
        return { outcome: 'error' };
    };

    const openContractDocument = async (tableRowId: number, fileId: number) => {
        if (
            !section.dataRequestProjectId ||
            !section.dataTableId ||
            section.fileColumnId == null
        ) {
            return;
        }
        const action = await dispatch(
            GetContractDocumentSource({
                dataRequestProjectId: section.dataRequestProjectId,
                dataTableId: section.dataTableId,
                fileColumnId: section.fileColumnId,
                tableRowId,
                fileId,
            })
        );
        if (GetContractDocumentSource.fulfilled.match(action)) {
            openUrlInNewTab(action.payload);
        }
    };

    const downloadSupportingDocument = async (
        tableRowId: number,
        documentKind: ThirdPartyPayoffDocumentKind
    ) => {
        const row = section.rows.find((r) => r.tableRowId === tableRowId);
        const document =
            documentKind === ThirdPartyPayoffDocumentKind.PayoffLetter
                ? row?.payoffLetterDocument
                : row?.payoffConfirmationDocument;
        if (!document) {
            return;
        }
        const action = await dispatch(
            DownloadThirdPartyPayoffSupportingDocument({ engagementId, tableRowId, documentKind })
        );
        if (DownloadThirdPartyPayoffSupportingDocument.fulfilled.match(action)) {
            openBlobInNewTab(action.payload, document.contentType, document.fileName);
        }
    };

    const saveWireInstructions = async (
        instructions: WireInstructions
    ): Promise<boolean> => {
        if (wireInstructionsRowId == null) {
            return false;
        }
        const action = await mutateWithSaveToast(
            dispatch(
                UpdateThirdPartyPayoffWireInstructions({
                    engagementId,
                    tableRowId: wireInstructionsRowId,
                    instructions,
                })
            ),
            UpdateThirdPartyPayoffWireInstructions.fulfilled.match
        );
        if (UpdateThirdPartyPayoffWireInstructions.fulfilled.match(action)) {
            setWireInstructionsRowId(null);
            return true;
        }
        return false;
    };

    const proposeRemoval = async (comment: string): Promise<boolean> => {
        if (removalRowId == null) {
            return false;
        }
        const action = await mutateWithSaveToast(
            dispatch(
                ProposeThirdPartyPayoffRemoval({
                    engagementId,
                    tableRowId: removalRowId,
                    comment,
                })
            ),
            ProposeThirdPartyPayoffRemoval.fulfilled.match
        );
        if (ProposeThirdPartyPayoffRemoval.fulfilled.match(action)) {
            setRemovalRowId(null);
            return true;
        }
        return false;
    };

    return (
        <div className="mt-4">
            <div className="ff-tickler-slot">
                <ClosingTickler closing={flowOfFunds.closing} />
            </div>

            <ThirdPartyPayoffs
                section={section}
                onSaveRow={saveRow}
                onOpenWireInstructions={setWireInstructionsRowId}
                onSupportingDocumentUpload={(tableRowId, documentKind, file) =>
                    mutateWithSaveToast(
                        dispatch(
                            UploadThirdPartyPayoffSupportingDocument({
                                engagementId,
                                tableRowId,
                                documentKind,
                                file,
                            })
                        ),
                        UploadThirdPartyPayoffSupportingDocument.fulfilled.match
                    )
                }
                onSupportingDocumentDelete={(tableRowId, documentKind) =>
                    mutateWithSaveToast(
                        dispatch(
                            DeleteThirdPartyPayoffSupportingDocument({
                                engagementId,
                                tableRowId,
                                documentKind,
                            })
                        ),
                        DeleteThirdPartyPayoffSupportingDocument.fulfilled.match
                    )
                }
                onSupportingDocumentDownload={downloadSupportingDocument}
                onApprovalChange={(tableRowId, isApproved) =>
                    mutateWithSaveToast(
                        dispatch(
                            SetThirdPartyPayoffApproval({
                                engagementId,
                                tableRowId,
                                targetState: toTargetApprovalState(isApproved),
                            })
                        ),
                        SetThirdPartyPayoffApproval.fulfilled.match
                    )
                }
                onBuyerAssumptionChange={async (tableRowId, isAssumedByBuyer) => {
                    const action = await mutateWithSaveToast(
                        dispatch(
                            SetThirdPartyPayoffBuyerAssumption({
                                engagementId,
                                tableRowId,
                                isAssumedByBuyer,
                            })
                        ),
                        SetThirdPartyPayoffBuyerAssumption.fulfilled.match
                    );
                    if (SetThirdPartyPayoffBuyerAssumption.fulfilled.match(action)) {
                        dispatch(GetFlowOfFunds(engagementId));
                    }
                }}
                onPayoffAmountChange={(tableRowId, payoffAmount) =>
                    mutateWithSaveToast(
                        dispatch(
                            SetThirdPartyPayoffPayoffAmount({
                                engagementId,
                                tableRowId,
                                payoffAmount,
                            })
                        ),
                        SetThirdPartyPayoffPayoffAmount.fulfilled.match
                    )
                }
                onOpenContractDocument={openContractDocument}
                onOpenRemovalProposalModal={setRemovalRowId}
                onRespondToRemoval={(tableRowId, isApproved) =>
                    mutateWithSaveToast(
                        dispatch(
                            RespondToThirdPartyPayoffRemoval({
                                engagementId,
                                tableRowId,
                                approve: isApproved,
                            })
                        ),
                        RespondToThirdPartyPayoffRemoval.fulfilled.match
                    )
                }
                onAddRow={addRow}
                onDaqiFilesChanged={() => dispatch(GetFlowOfFunds(engagementId))}
            />

            <WireInstructionsModal
                isOpen={wireInstructionsRowId != null}
                instructions={wireInstructionsRow?.wireInstructions ?? null}
                isReadOnly={
                    !wireInstructionsRow?.capabilities.canEditWireInstructions ||
                    !!wireInstructionsRow?.isAssumedByBuyer
                }
                onClose={() => setWireInstructionsRowId(null)}
                onSave={saveWireInstructions}
            />
            <RemovalProposalModal
                isOpen={removalRowId != null}
                onClose={() => setRemovalRowId(null)}
                onSubmit={proposeRemoval}
            />
        </div>
    );
};

export default ThirdPartyPayoffsSubTab;
